/**
 * 词条加载器
 *
 * 抽离自 apps/web 与 apps/accounts 中重复的远程词条加载逻辑：
 * - loadLocaleManifest：拉取 manifest.json（hash 用于 cache-busting），带重试
 * - loadApiLocale：拉取后端 API 词条并合并进实例（测试环境用）
 */
import type I18nKit from './index'

/**
 * 拉取 locales/manifest.json
 *
 * @returns 形如 `{ "zh-CN": "abc123", "en-US": "def456" }` 的 hash 映射；失败返回 `{}`
 *
 * 内置重试与 cache-busting（`?v=${version}`），统一两个 app 的健壮性。
 */
export async function loadLocaleManifest(opts: {
  /** locales 目录的 URL 前缀。web: `''`；accounts: `/accounts` */
  basePath?: string
  /** 版本号，用于 cache-busting */
  version?: string
  /** 重试次数，默认 2 */
  retries?: number
}): Promise<Record<string, string>> {
  const { basePath = '', version, retries = 2 } = opts
  const path = `${basePath}/locales/manifest.json`
  const query = version ? `?v=${version}` : `?v=${Date.now()}`

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  let attempt = 0
  while (attempt <= retries) {
    try {
      const res = await fetch(`${path}${query}`)
      if (!res.ok) {
        throw new Error(`load manifest failed: ${res.status}`)
      }
      return await res.json()
    } catch {
      if (attempt === retries) {
        return {}
      }
      await sleep(300 * (attempt + 1))
      attempt += 1
    }
  }
  return {}
}

/**
 * 拉取后端 API 词条并合并进 I18nKit 实例
 *
 * 测试环境（dev/sit/sit1/pre）用：拉取后端实时词条并通过 `addResources` 合并，
 * 用于覆盖/补充静态词条文件。
 */
export async function loadApiLocale(
  instance: I18nKit,
  opts: {
    locale: string
    /** 项目类型，决定后端返回哪一套词条，如 `peerup` / `accounts` */
    projectType: string
    /** 完整的 API 地址；不传则用默认模板拼接 */
    url?: string
  },
): Promise<void> {
  const { locale, projectType, url } = opts
  const apiUrl
    = url
      ?? `https://www.dev.peerup.com/api/main/common/getI18nDict?locale=${locale}&projectType=${projectType}`

  try {
    const response = await fetch(apiUrl)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    if (data.code !== 10000) {
      throw new Error(data.sub_msg || data.msg || '获取语言包失败')
    }

    const apiLocale = data.data as Record<string, unknown>
    if (apiLocale && Object.keys(apiLocale).length > 0) {
      instance.addResources(locale, 'translation', apiLocale)
    }
  } catch (error) {
    console.error(`[i18n-kit] 加载 API 语言包失败 (projectType=${projectType}, locale=${locale})`, error)
  }
}
